import { useTheme } from '@emotion/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button, CircularProgress, Divider, Grid, IconButton, Typography } from '@mui/material'
import { IconArrowLeft, IconCheck, IconSearch } from '@tabler/icons-react'
import { useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { Input, Select } from '~/components'
import { brazilStates } from '~/constants'
import { useCep } from '~/hooks'
import { addressSchema } from '~/utils/yupSchema'
import * as yup from 'yup'
import { useIndicationFlow } from '~/contexts'
import { AddressGraphqlDto, EIndicationStatus, useGetProposalAndIndicationByIdLazyQuery, useUpdateIndicationMutation } from '~/graphql/types'
import { clearString } from '~/utils'
import { toast } from 'react-toastify'
import { AddressPendency, hasPendencyOrNullOnAddress } from '../utils'
import { Link } from 'react-router-dom'

type FormData = {
  address: {
    zipcode: string
    state: string
    city: string
    neighborhood: string
    street: string
    number: string
    complement?: string
  }
}

const schema = yup
  .object({
    address: addressSchema
  })
  .required()

export const Address: React.FC = () => {
  const [consistencyWaiting, setConsistencyWaiting] = useState(false)
  const theme = useTheme()
  const { selectedIndication, setSelectedIndication, selectedContract, setSelectedContract, goToPage, selectedCustomer } = useIndicationFlow()
  const formMethods = useForm<FormData>({
    resolver: yupResolver(schema)
  })
  const { address } = useWatch({ control: formMethods.control })
  const { isLoading: fetchCepIsLoading, fetchCep } = useCep({ showErrorMessage: true })

  const onSearchCep = useCallback(async () => {
    if (address?.zipcode) {
      const { data, isSuccess } = await fetchCep(address.zipcode)
      if (isSuccess) {
        formMethods.setValue('address.state', data?.uf || '')
        formMethods.setValue('address.city', data?.localidade || '')
        formMethods.setValue('address.neighborhood', data?.bairro || '')
        formMethods.setValue('address.street', data?.logradouro || '')
      }
    }
  }, [address])

  useEffect(() => {
    if (selectedIndication) {
      formMethods.setValue('address.zipcode', selectedCustomer?.address?.zipcode || '')
      formMethods.setValue('address.state', selectedCustomer?.address?.state || '')
      formMethods.setValue('address.city', selectedCustomer?.address?.city || '')
      formMethods.setValue('address.neighborhood', selectedCustomer?.address?.neighborhood || '')
      formMethods.setValue('address.street', selectedCustomer?.address?.street || '')
      formMethods.setValue('address.number', selectedCustomer?.address?.number || '')
      formMethods.setValue('address.complement', selectedCustomer?.address?.complement || '')

      const fieldsToSetError: AddressPendency[] = ['city', 'neighborhood', 'number', 'state', 'street', 'zipCode']

      fieldsToSetError.forEach(item => {
        // Map contract 'zipCode' pendency to form 'zipcode' field
        const fieldNameOnForm = item === 'zipCode' ? 'zipcode' : item
        if (selectedContract?.customerRef?.pendencies?.[item]?.rejectedAt) {
          formMethods.setError(`address.${fieldNameOnForm}` as any, { message: 'Este campo foi reprovado por um analista!' })
        }
      })
    }
  }, [])

  const [getProposalAndIndicationById, { loading: getProposalAndIndicationByIdIsLoading }] = useGetProposalAndIndicationByIdLazyQuery({
    onCompleted(data) {
      if (data.getIndicationRelatedEntitiesById.contract?._id && data.getIndicationById._id) {
        setSelectedIndication(data.getIndicationById)
        setSelectedContract(data.getIndicationRelatedEntitiesById.contract)
      }
      if (data.getIndicationRelatedEntitiesById.contract?._id) {
        setSelectedContract(data.getIndicationRelatedEntitiesById.contract)
      }
      toast.success('Endereço salvo com sucesso!')
      // Avança para o próximo step após Endereço
      goToPage(2)
    },
  })

  const [updateIndication, { loading: updateIndicationIsLoading }] = useUpdateIndicationMutation()

  const handleSubmit = useCallback(async (data: FormData) => {
    // Envia sempre todos os campos do endereço
    const addressParams: AddressGraphqlDto = {
      zipcode: clearString(data.address.zipcode || ''),
      state: data.address.state,
      city: data.address.city,
      neighborhood: data.address.neighborhood,
      street: data.address.street,
      number: data.address.number,
      complement: data.address.complement || undefined,
      country: 'Brasil'
    }

    if (selectedIndication?._id) {
      await updateIndication({
        variables: {
          params: {
            id: selectedIndication._id,
            address: addressParams
          }
        }
      })

      // Aguarda 5s para consistência do backend antes de refazer a consulta
      setConsistencyWaiting(true)
      await new Promise(resolve => setTimeout(resolve, 5000))
      await getProposalAndIndicationById({ variables: { id: selectedIndication._id } })
      setConsistencyWaiting(false)
    }
  }, [selectedIndication, updateIndication, getProposalAndIndicationById])

  const allowedStatusToUpdate = [
    EIndicationStatus.requestedProposal,
    EIndicationStatus.contractWithPendencies,
  ];
  const isStatusAllowed = allowedStatusToUpdate.includes(
    selectedIndication?.status as EIndicationStatus,
  );

  const enableSubmit = isStatusAllowed && Boolean(selectedIndication?._id)

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit((data) => handleSubmit(data))}>
        <Typography sx={{ paddingBottom: '1rem' }} variant='h3' fontWeight={500} color={theme.palette.grey[800]}>Endereço do cliente</Typography>

        <Grid container spacing={3}>
          <Grid xs={12} md={4} item>
            <Input
              icons={{
                end: {
                  element: fetchCepIsLoading ? (
                    <CircularProgress size={18} color='inherit' />
                  ) : (
                    <IconButton disabled={hasPendencyOrNullOnAddress('zipCode', selectedContract?.customerRef.pendencies)} onClick={() => onSearchCep()}>
                      <IconSearch />
                    </IconButton>
                  )
                }
              }}
              mask='cep'
              name='address.zipcode'
              label='CEP'
              disabled={hasPendencyOrNullOnAddress('zipCode', selectedContract?.customerRef.pendencies)}
            />
          </Grid>

          <Grid item xs={12} md={4} sx={{ paddingTop: '0 !important' }}></Grid>
          <Grid item xs={12} md={4} sx={{ paddingTop: '0 !important' }}></Grid>

          <Grid item xs={12} md={4}>
            <Input disabled={hasPendencyOrNullOnAddress('street', selectedContract?.customerRef.pendencies)} name='address.street' label='Rua' />
          </Grid>
          <Grid item xs={12} md={4}>
            <Input disabled={hasPendencyOrNullOnAddress('number', selectedContract?.customerRef.pendencies)} name='address.number' label='Número' />
          </Grid>
          <Grid item xs={12} md={4}>
            <Input name='address.complement' label='Complemento (Opcional)' />
          </Grid>
          <Grid item xs={12} md={4}>
            <Input disabled={hasPendencyOrNullOnAddress('neighborhood', selectedContract?.customerRef.pendencies)} name='address.neighborhood' label='Bairro' />
          </Grid>
          <Grid item xs={12} md={4}>
            <Input disabled={hasPendencyOrNullOnAddress('city', selectedContract?.customerRef.pendencies)} name='address.city' label='Cidade' />
          </Grid>
          <Grid item xs={12} md={4}>
            <Select disabled={hasPendencyOrNullOnAddress('state', selectedContract?.customerRef.pendencies)} name='address.state' label='UF' options={brazilStates} />
          </Grid>
        </Grid>

        <Divider sx={{ margin: '2rem 0' }} />

        <Box sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Link to='/app/indications'>
            <Button startIcon={<IconArrowLeft />} color='secondary'>
              Voltar
            </Button>
          </Link>

          <Button
            disabled={!enableSubmit || updateIndicationIsLoading || getProposalAndIndicationByIdIsLoading || consistencyWaiting}
            type='submit'
            startIcon={(updateIndicationIsLoading || getProposalAndIndicationByIdIsLoading || consistencyWaiting) ? <CircularProgress size={24} color='inherit' /> : <IconCheck size={24} />}
          >
            Confirmar
          </Button>
        </Box>
      </form>
    </FormProvider>
  )
}